import {
  AQUA_BLUE,
  FilterType,
  Table as WWTable,
  useMultiselection,
} from "@120wateraudit/waterworks";
import React, { useCallback, useMemo, useContext } from "react";
import { useOpenStatusChangeModal } from "./useOpenStatusChangeModal";
import { intersection } from "lodash";
import { TableContext } from "src/hooks/useTableState";
import Sort from "src/types/Sort";
import { CNCSubmission, SubmissionStatus } from "src/types/Submission";
import { gotoSystem } from "src/utils/system";
import { ModalContext } from "src/modules/Modal";
import ActionMenu from "src/components/ActionMenu";
import { trueFalseListOptions } from "src/utils/selectList";
import { formatDate } from "src/utils/format";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import AssignedToDropdown from "./AssignedToDropdown";
import {
  CncPwsSubmissionPeriod,
  PwsSubmissionPeriod,
  useGetCurrentPrimacyAgencyQuery,
  useGetPwsCNCSubmissionPeriodsQuery,
} from "src/services/state-submission";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIsWrite, User } from "../User";
import LinkCell from "src/components/Cells/LinkCell";
import SubmissionStatusDropdown from "./SubmissionStatusDropdown";
import {
  useGetAccountUsersQuery,
  useGetAvailableCountiesQuery,
} from "src/services";
import VisuallyHidden from "src/components/VisuallyHidden";

type PwsSubmissionPeriodTableData = CncPwsSubmissionPeriod & { id: number };

interface TableProps {
  tableContext: TableContext;
  submissionPeriodId: number;
  isOnUtilityDetailsPage?: boolean;
}

const NoRowHover = styled.div`
  .wtrwks-table-body tr:hover td {
    box-shadow: none;
  }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  &:hover {
    color: ${AQUA_BLUE};
    cursor: pointer;
    transform: scale(1.2);
    transition: transform 0.2s ease-in-out;
  }
`;

export const CustomerNoticeCertificationsTable: React.FC<TableProps> = ({
  tableContext,
  submissionPeriodId,
  isOnUtilityDetailsPage,
}) => {
  const isWrite = useIsWrite();

  const { params, setFilters, setPage, setPageSize, setSearchTerm, setSort } =
    tableContext;

  const { data: primacyAgency } = useGetCurrentPrimacyAgencyQuery(undefined);

  const { data: accountUsers } = useGetAccountUsersQuery({
    accountId: primacyAgency?.accountId ?? 0,
    permissions: ["stateportal:write"],
    excludePermissions: ["platform:admin"],
  });

  const { onClearSelection, onSelect, onSelectAll, selected, selectedIds } =
    useMultiselection<PwsSubmissionPeriodTableData>();

  const { closeModal, openModal } = useContext(ModalContext);

  const { data: counties = [] } = useGetAvailableCountiesQuery();

  const { data: submissionQueryData, refetch } =
    useGetPwsCNCSubmissionPeriodsQuery({
      submissionPeriodId,
      searchParams: params,
    });

  const openChangeSubmissionCommunicationStatus = useOpenStatusChangeModal();

  const openViewCommunicationStatus = useCallback(
    (
      submissionCommunication: CNCSubmission,
      accountId: number,
      pwsName: string,
    ) =>
      openModal("viewConsumerNoticeModalV2", {
        closeModal: () => {
          closeModal();
        },
        cncSubmission: submissionCommunication,
        accountId,
        pwsName,
      }),
    [openModal, closeModal],
  );

  const changeAssignee = useCallback(
    () =>
      openModal("changeAssigneeModal", {
        closeModal: () => {
          onClearSelection();
          closeModal();
        },
        submissionIds: selected.map((s) => s.activeSubmissionId),
      }),
    [openModal, closeModal, selected],
  );

  const openChangeSubmissionStatus = useCallback(
    () =>
      openModal("changeSubmissionStatusModal", {
        closeModal: () => {
          onClearSelection();
          closeModal();
        },
        selected,
      }),
    [openModal, closeModal, selected],
  );

  const submissions: PwsSubmissionPeriodTableData[] =
    submissionQueryData?.data?.map(
      (psp: PwsSubmissionPeriod): PwsSubmissionPeriodTableData => ({
        ...psp,
        id: Number(`${psp.pwsId}.${psp.submissionPeriodId}`),
        activeSubmission: psp.activeSubmission as CNCSubmission,
      }),
    ) ?? [];

  const areAllSelected = useMemo(() => {
    const idsOnPage = submissions.map((s) => s.id);
    return intersection(idsOnPage, selectedIds).length === idsOnPage.length;
  }, [submissions, selectedIds]);

  const selectAllData = useCallback(() => {
    onSelectAll(submissions, areAllSelected);
  }, [onSelectAll, submissions, areAllSelected]);

  const COLUMNS = [
    {
      Header: "PWS",
      key: "system.name",
      name: "PWS",
      sortable: true,
      accessor: ({ system }: PwsSubmissionPeriodTableData) => (
        <div>
          {system.id ? (
            <LinkCell text={system.name ?? ""} to={`/utilities/${system.id}`} />
          ) : (
            <p>{system.name ?? ""}</p>
          )}
          <p>{system.pwsId}</p>
        </div>
      ),
      hiddenOnColumnPicker: true,
    },
    {
      Header: "Submitted By",
      name: "Submitted By",
      key: "activeSubmission.cncSubmission.submitterName",
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.submitterName ?? "--",
    },
    {
      Header: "Submitted Date",
      name: "Submitted Date",
      key: "submittedOn",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        formatDate(s.activeSubmission?.submittedOn) ?? "--",
    },
    {
      Header: "Last Submitted Lead",
      name: "Last Submitted Lead",
      key: "activeSubmission.cncSubmission.lastSubmittedInventoryLeadCount",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.lastSubmittedInventoryLeadCount ?? "--",
    },
    {
      Header: "Last Submitted GRR",
      name: "Last Submitted GRR",
      key: "activeSubmission.cncSubmission.lastSubmittedInventoryGrrCount",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.lastSubmittedInventoryGrrCount ?? "--",
    },
    {
      Header: "Last Submitted Unknown",
      name: "Last Submitted Unknown",
      key: "activeSubmission.cncSubmission.lastSubmittedInventoryUnknownCount",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.lastSubmittedInventoryUnknownCount ?? "--",
    },
    {
      Header: "Status",
      name: "Status",
      key: "status",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) => (
        <SubmissionStatusDropdown
          openChangeSubmissionStatus={openChangeSubmissionCommunicationStatus}
          status={s.activeSubmission?.status ?? "notSubmitted"}
          submissionId={s.activeSubmission?.id ?? 0}
          pwsName={s.system.name ?? ""}
          pwsId={s.pwsId ?? 0}
          submissionPeriodId={s.submissionPeriodId ?? 0}
          tooltipText="Change the Customer Notice Submission Status"
          refetch={refetch}
        />
      ),
    },
    {
      Header: "Overdue",
      name: "Overdue",
      key: "overdue",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) => (s.overdue ? "Yes" : "No"),
    },
    {
      Header: "Assigned To",
      name: "Assigned To",
      key: "activeSubmission.assigneeLastName",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) => {
        return (
          <AssignedToDropdown
            primacyAgencyAccountId={primacyAgency?.accountId}
            submission={s.activeSubmission}
          />
        );
      },
    },
    {
      Header: "Distribution Method",
      name: "Distribution Method",
      key: "distributionMethod",
      sortable: false,
      accessor: (s: PwsSubmissionPeriodTableData) => (
        <p>
          {[
            s.activeSubmission?.data?.mailedWithWaterBill &&
              "Mailed with Water Bill",
            s.activeSubmission?.data?.mailedWithCCR && "Mailed with CCR",
            s.activeSubmission?.data?.separateMail && "Separate Mail",
            s.activeSubmission?.data?.handDelivery && "Hand Delivery",
            s.activeSubmission?.data?.email && "Email",
            s.activeSubmission?.data?.otherDeliveryMethod && "Other",
            s.activeSubmission?.data?.deliveryMethodsNotRequired &&
              "No Notices Required",
          ]
            .filter(Boolean)
            .join(", ") || "N/A"}
        </p>
      ),
    },
    {
      Header: "Lead Notice Quantity",
      name: "Lead Notice Quantity",
      key: "activeSubmission.cncSubmission.leadNoticeDistributions",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.leadNoticeDistributions?.toString() ?? "--",
    },
    {
      Header: "Lead Notice Date",
      name: "Lead Notice Date",
      key: "activeSubmission.cncSubmission.leadNoticeDistributionsDate",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        formatDate(s.activeSubmission?.data?.leadNoticeDistributionsDate) ??
        "--",
    },
    {
      Header: "GRR Notice Quantity",
      name: "GRR Notice Quantity",
      key: "activeSubmission.cncSubmission.grrNoticeDistributions",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.grrNoticeDistributions?.toString() ?? "--",
    },
    {
      Header: "GRR Notice Date",
      name: "GRR Notice Date",
      key: "activeSubmission.cncSubmission.grrNoticeDistributionsDate",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        formatDate(s.activeSubmission?.data?.grrNoticeDistributionsDate) ??
        "--",
    },
    {
      Header: "Unknown Notice Quantity",
      name: "Unknown Notice Quantity",
      key: "activeSubmission.cncSubmission.unknownNoticeDistributions",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data?.unknownNoticeDistributions?.toString() ??
        "--",
    },
    {
      Header: "Unknown Notice Date",
      name: "Unknown Notice Date",
      key: "activeSubmission.cncSubmission.unknownNoticeDistributionsDate",
      sortable: true,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        formatDate(s.activeSubmission?.data?.unknownNoticeDistributionsDate) ??
        "--",
    },
    {
      Header: "Counties Served",
      name: "Counties Served",
      key: "counties",
      sortable: false,
      accessor: (s: PwsSubmissionPeriodTableData) => (
        <p>
          {s.system.counties
            ? s.system.counties
                .map((c) => c.shortName)
                .sort()
                .join(", ")
            : "N/A"}
        </p>
      ),
    },
    {
      Header: "Certificate Responses",
      name: "Certificate Responses",
      key: "certificateResponses",
      sortable: false,
      accessor: (s: PwsSubmissionPeriodTableData) =>
        s.activeSubmission?.data ? (
          <StyledFontAwesomeIcon
            onClick={(e) => {
              e.stopPropagation();
              openViewCommunicationStatus(
                s.activeSubmission,
                s.system?.accountId ?? 0,
                s.system?.name ?? "N/A",
              );
            }}
            icon={faFile}
            aria-label="View communication status"
          />
        ) : (
          "--"
        ),
    },
  ];

  const TABLE_ACTIONS = useMemo(() => {
    const actions = isWrite
      ? [
          {
            label: "Change Submission Status",
            onClick: openChangeSubmissionStatus,
          },
          {
            label: "Change Assignee",
            onClick: changeAssignee,
          },
        ]
      : [];
    return actions;
  }, [isWrite, openChangeSubmissionStatus, changeAssignee]);

  const FILTERS = [
    {
      label: "Status",
      key: "status",
      type: FilterType.ListSelection,
      defaultValue: "All",
      multipleSelections: false,
      scrollingSelections: true,
      options: Object.entries(SubmissionStatus).map(([key, val]) => ({
        id: key,
        value: key,
        text: val,
      })),
    },
    {
      label: "Last Submitted Lead",
      key: "lastSubmittedLead",
      type: FilterType.Number,
    },
    {
      label: "Last Submitted GRR",
      key: "lastSubmittedGRR",
      type: FilterType.Number,
    },
    {
      label: "Last Submitted Unknown",
      key: "lastSubmittedUnknown",
      type: FilterType.Number,
    },
    {
      label: "Lead Notice Quantity",
      key: "leadNoticeDistributions",
      type: FilterType.Number,
    },
    {
      label: "Lead Notice Date",
      key: "leadNoticeDistributionsDate",
      type: FilterType.DatePicker,
    },
    {
      label: "GRR Notice Quantity",
      key: "grrNoticeDistributions",
      type: FilterType.Number,
    },
    {
      label: "GRR Notice Date",
      key: "grrNoticeDistributionsDate",
      type: FilterType.DatePicker,
    },
    {
      label: "Unknown Notice Quantity",
      key: "unknownNoticeDistributions",
      type: FilterType.Number,
    },
    {
      label: "Unknown Notice Date",
      key: "unknownNoticeDistributionsDate",
      type: FilterType.DatePicker,
    },
    {
      label: "Counties Served",
      key: "countiesServed",
      type: FilterType.ListSelection,
      multipleSelections: true,
      scrollingSelections: true,
      options: counties?.map((c) => ({
        id: c.countyFIPS,
        value: c.countyFIPS,
        text: `${c.shortName} (${c.countyFIPS})`,
      })),
    },
    {
      label: "Overdue",
      key: "overdue",
      type: FilterType.ListSelection,
      defaultValue: "All",
      multipleSelections: false,
      options: trueFalseListOptions,
    },
    {
      label: "Assigned To",
      key: "assigneeUserId",
      type: FilterType.ListSelection,
      options: [
        {
          id: "unassigned",
          value: "null" as any,
          text: "Unassigned",
        },
      ].concat(
        accountUsers?.items?.map((u: User) => ({
          id: `user-${u.id}`,
          value: u.id,
          text: `${u.lastName}, ${u.firstName}`,
        })) ?? [],
      ),
    },

    {
      label: "Submitted By",
      key: "submittedBy",
      type: FilterType.TextMatch,
    },
    {
      label: "Submitted Date",
      key: "submittedOn",
      type: FilterType.DatePicker,
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const ROW_ACTIONS_COLUMN = [
    {
      Header: <VisuallyHidden>Actions</VisuallyHidden>,
      hiddenOnColumnPicker: true,
      key: "system.id",
      sortable: false,
      accessor: (psp: PwsSubmissionPeriodTableData) => {
        const primacyAgencyId = psp.submissionPeriod.primacyAgencyId;
        return (
          psp.activeSubmission && (
            <ActionMenu
              identifiers={{
                accountId: psp.system.accountId.toString(),
                submissionId: psp.activeSubmissionId.toString(),
              }}
              actions={getRowActions({ ...psp, primacyAgencyId })}
              ariaLabel="Certification actions"
            />
          )
        );
      },
    },
  ];

  const getRowActions = (submission: PwsSubmissionPeriodTableData): any => {
    const actions = [];
    actions.push({
      text: "Login to PWS Portal",
      onClick: gotoSystem,
      identifier: "accountId",
    });
    actions.push({
      text: "Show Activity",
      onClick: () =>
        openModal(
          "activityModal",
          {
            submission,
            modalSize: "large",
            onClose: async () => {
              closeModal();
            },
          },
          "window",
        ),
      identifier: "submissionId",
    });
    return actions;
  };

  return (
    <NoRowHover>
      <WWTable
        activeFilters={params.filters}
        allowHiddenColumns={true}
        columns={[...COLUMNS]}
        rowActionColumn={ROW_ACTIONS_COLUMN}
        data={submissions}
        filterable
        filters={[...FILTERS]}
        itemName="Submissions"
        loading={false}
        onFilterChanged={setFilters}
        onPageChanged={setPage}
        onPageSizeChanged={setPageSize}
        orderable={true}
        onSearchChanged={setSearchTerm}
        onSortChanged={setSort}
        page={params.page}
        pageSize={params.pageSize}
        paginated
        selected={selected}
        searchTerm={params.searchTerm}
        searchable
        sort={params.sort as Sort}
        sortable
        totalRecords={submissionQueryData?.count ?? 0}
        actions={TABLE_ACTIONS}
        areAllSelected={areAllSelected}
        selectionMode="multi"
        onClearSelection={onClearSelection}
        onSelect={onSelect}
        onSelectAll={selectAllData}
        maxHeight={"57vh"}
        stickyFirstColumn
        stickyHeader
        showOverflow={false}
      />
    </NoRowHover>
  );
};

export default CustomerNoticeCertificationsTable;
